/**
 * Chunked Upload Utility
 * 
 * Uploads files in small batches to avoid connection resets and timeouts.
 * Provides progress callbacks for UI updates.
 */

import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Upload a single file to the server with retry logic
 * @param {File} file - File to upload
 * @param {string} token - Auth token
 * @param {string} sessionId - Session ID to group files
 * @param {number} maxRetries - Maximum retry attempts (default: 2)
 * @returns {Promise<{file_id: string, filename: string}>}
 */
const uploadSingleFile = async (file, token, sessionId, maxRetries = 2) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('session_id', sessionId);

  // Calculate timeout based on file size (min 2 min, max 10 min)
  // Assuming ~500KB/s upload speed as baseline
  const fileSizeMB = file.size / (1024 * 1024);
  const estimatedSeconds = Math.max(120, Math.min(600, fileSizeMB * 3));
  const timeout = estimatedSeconds * 1000;

  let lastError = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        console.log(`🔄 Retry ${attempt}/${maxRetries} for ${file.name}`);
        // Wait before retry (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
      }

      const response = await axios.post(
        `${API}/api/documents/upload-single`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          timeout: timeout,
          maxContentLength: Infinity,
          maxBodyLength: Infinity
        }
      );

      return {
        file_id: response.data.file_id,
        filename: response.data.filename,
        size: response.data.size
      };
    } catch (error) {
      lastError = error;
      console.error(`❌ Upload attempt ${attempt + 1} failed for ${file.name}:`, error.message);
      
      // Don't retry on certain errors
      if (error.response?.status === 400 || error.response?.status === 413) {
        throw error; // File too large or invalid - don't retry
      }
    }
  }

  throw lastError;
};

/**
 * Upload files in chunks with progress tracking
 * 
 * @param {File[]} files - Array of files to upload
 * @param {Object} options - Upload options
 * @param {number} options.chunkSize - Files per chunk (default: 3 for reliability)
 * @param {function} options.onFileStart - Called when file upload starts
 * @param {function} options.onFileComplete - Called when file upload completes
 * @param {function} options.onFileError - Called when file upload fails
 * @param {function} options.onProgress - Called with overall progress
 * @param {function} options.onChunkComplete - Called when a chunk completes
 * @returns {Promise<{fileIds: string[], failed: string[]}>}
 */
export const uploadFilesChunked = async (files, options = {}) => {
  // Calculate average file size for logging
  const totalSize = files.reduce((sum, f) => sum + f.size, 0);
  const avgSizeMB = (totalSize / files.length) / (1024 * 1024);
  
  // IMPORTANT: Upload one file at a time to avoid overwhelming the server
  // Previous parallel uploads were causing 502/520 errors in production
  const defaultChunkSize = 1;  // Sequential uploads only
  
  const {
    chunkSize = defaultChunkSize,
    onFileStart = () => {},
    onFileComplete = () => {},
    onFileError = () => {},
    onProgress = () => {},
    onChunkComplete = () => {}
  } = options;

  console.log(`📊 Upload config: ${files.length} files, avg size: ${avgSizeMB.toFixed(1)}MB, chunk size: ${chunkSize} (sequential)`);

  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not authenticated');
  }

  const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const fileIds = [];
  const failed = [];
  const totalFiles = files.length;
  let completedFiles = 0;

  // Process files in chunks
  for (let i = 0; i < files.length; i += chunkSize) {
    const chunk = files.slice(i, i + chunkSize);
    const chunkIndex = Math.floor(i / chunkSize) + 1;
    const totalChunks = Math.ceil(files.length / chunkSize);

    // Upload chunk files in parallel
    const chunkPromises = chunk.map(async (file, indexInChunk) => {
      const fileIndex = i + indexInChunk;
      
      try {
        // Notify file start
        onFileStart({
          file,
          index: fileIndex,
          total: totalFiles
        });

        // Upload the file
        const result = await uploadSingleFile(file, token, sessionId);
        
        completedFiles++;
        fileIds.push(result.file_id);

        // Notify file complete
        onFileComplete({
          file,
          index: fileIndex,
          total: totalFiles,
          fileId: result.file_id,
          completed: completedFiles
        });

        // Update overall progress
        onProgress({
          completed: completedFiles,
          total: totalFiles,
          percent: Math.round((completedFiles / totalFiles) * 100)
        });

        return result;

      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        failed.push(file.name);

        // Notify file error
        onFileError({
          file,
          index: fileIndex,
          error: error.message || 'Upload failed'
        });

        completedFiles++;
        onProgress({
          completed: completedFiles,
          total: totalFiles,
          percent: Math.round((completedFiles / totalFiles) * 100)
        });

        return null;
      }
    });

    // Wait for all files in this chunk to complete
    await Promise.all(chunkPromises);

    // Notify chunk complete
    onChunkComplete({
      chunkIndex,
      totalChunks,
      filesInChunk: chunk.length
    });
    
    // Add delay between uploads to let the server process and free memory
    // Reduced to 500ms for faster uploads while still preventing overload
    if (i + chunkSize < files.length) {
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }

  return {
    fileIds,
    failed,
    sessionId,
    totalUploaded: fileIds.length,
    totalFailed: failed.length
  };
};

/**
 * Start extraction job with uploaded file IDs
 * 
 * @param {string[]} fileIds - Array of file IDs from upload
 * @param {Object} options - Extraction options
 * @param {string} options.workflowType - 'apf' or 'retail'
 * @param {string} options.stateCode - State code (TS, AP, KA)
 * @param {string} options.bankFormat - Bank format (for APF)
 * @returns {Promise<{job_id: string, status: string}>}
 */
export const startExtraction = async (fileIds, options = {}) => {
  const { workflowType = 'retail', stateCode, bankFormat } = options;

  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Not authenticated');
  }

  const formData = new FormData();
  formData.append('file_ids', fileIds.join(','));
  formData.append('workflow_type', workflowType);
  
  if (stateCode) {
    formData.append('state_code', stateCode);
  }
  if (bankFormat) {
    formData.append('bank_format', bankFormat);
  }

  const response = await axios.post(
    `${API}/api/documents/start-extraction`,
    formData,
    {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }
  );

  return {
    job_id: response.data.job_id,
    status: response.data.status,
    files_count: response.data.files_count
  };
};

export default {
  uploadFilesChunked,
  startExtraction
};
